import { Handle, Position } from "@xyflow/react";
import { Table2 } from "lucide-react";
import type { Entity, EntityField } from "@/lib/arch-parser";
import { cn } from "@/lib/utils";
import { containerPalette } from "./node-colors";

// ERD reading order: primary keys first, then foreign keys, then the rest —
// each group stable in its authored order. Mirrors the reference ERD where the
// key block sits at the top of every table. Presentation-only; the underlying
// entity data is never reordered.
export function orderFieldsKeysFirst(fields: ReadonlyArray<EntityField>): EntityField[] {
  const pk = fields.filter((f) => f.pk);
  const fk = fields.filter((f) => !f.pk && f.fk);
  const rest = fields.filter((f) => !f.pk && !f.fk);
  return [...pk, ...fk, ...rest];
}

export interface EntityNodeData {
  entity: Entity;
  // "simple" = header only (conceptual business entity); "detailed" = header
  // plus field rows with PK/FK glyphs (physical ERD). Driven by the
  // dataModelDensity toggle in the Breadcrumb.
  density: "simple" | "detailed";
  faded?: boolean;
  highlighted?: boolean;
  [key: string]: unknown;
}

const SIDES = ["top", "bottom", "left", "right"] as const;

// Entities live inside a data-storage container, so they wear the same
// "data lives here" datastore palette as their parent.
const PALETTE = containerPalette("database");

export function EntityNode({ data, selected }: { data: EntityNodeData; selected?: boolean }) {
  const { entity, density } = data;
  const fields = entity.fields ?? [];
  const showFields = density === "detailed" && fields.length > 0;
  const ordered = showFields ? orderFieldsKeysFirst(fields) : fields;
  // Divider sits after the last key (PK/FK) row, so the key block reads as a
  // unit. 0 when there are no keys (no divider) or all rows are keys.
  const keyCount = ordered.filter((f) => f.pk || f.fk).length;

  return (
    <div
      className={cn(
        "relative rounded-md border-[1.5px] shadow-sm transition-all min-w-[200px] max-w-[300px] overflow-hidden",
        PALETTE.bg,
        PALETTE.border,
        PALETTE.text,
        "opacity-90",
        selected &&
          "ring-2 ring-offset-2 ring-offset-background ring-[color:var(--node-selection-ring)] shadow-md opacity-100 z-10",
        data.faded && "opacity-30",
        data.highlighted && "ring-2 ring-teal-400/70 opacity-100",
      )}
    >
      {SIDES.map((side) => (
        <Handle
          key={`t-${side}`}
          type="target"
          position={positionFor(side)}
          id={side}
          className="!bg-foreground/30"
        />
      ))}

      {/* Header */}
      <div
        className={cn(
          "flex items-center gap-1.5 px-2.5 py-1.5",
          showFields && "border-b border-current/15",
        )}
      >
        <Table2 className="h-3 w-3 shrink-0" />
        <div
          className="font-semibold leading-none tracking-tight"
          style={{ fontSize: "calc(12px * var(--archviz-label-scale, 1))" }}
        >
          {entity.label}
        </div>
      </div>

      {/* Simple view: conceptual description under the header. */}
      {!showFields && entity.description && (
        <div className="px-2.5 pb-1.5 text-[10px] text-muted-foreground leading-snug">
          {entity.description}
        </div>
      )}

      {/* Detailed view: ERD field rows in an aligned PK/FK │ name │ type grid.
          Vertical column rules + a divider under the key block echo the
          reference ERD's table layout. */}
      {showFields && (
        <div className="text-[10px]">
          {ordered.map((field, i) => (
            <div
              key={field.name}
              className={cn(
                "grid grid-cols-[1.75rem_minmax(0,1fr)_auto] items-stretch leading-none",
                keyCount > 0 && keyCount < ordered.length && i === keyCount - 1
                  ? "border-b border-current/20"
                  : undefined,
              )}
            >
              <span
                className={cn(
                  "flex items-center justify-center py-1 font-mono text-[8px] font-semibold tracking-wide",
                  field.pk ? "text-amber-500" : field.fk ? "text-sky-500" : undefined,
                )}
              >
                {field.pk ? "PK" : field.fk ? "FK" : ""}
              </span>
              <span
                className={cn(
                  "truncate border-l border-current/10 px-2 py-1 font-mono",
                  field.pk && "font-semibold",
                )}
              >
                {field.name}
              </span>
              <span className="whitespace-nowrap border-l border-current/10 px-2 py-1 text-right font-mono text-ink-subtle">
                {field.type ?? ""}
              </span>
            </div>
          ))}
        </div>
      )}

      {SIDES.map((side) => (
        <Handle
          key={`s-${side}`}
          type="source"
          position={positionFor(side)}
          id={`${side}-src`}
          className="!bg-foreground/30"
        />
      ))}
    </div>
  );
}

function positionFor(side: (typeof SIDES)[number]): Position {
  switch (side) {
    case "top":
      return Position.Top;
    case "bottom":
      return Position.Bottom;
    case "left":
      return Position.Left;
    case "right":
      return Position.Right;
  }
}
